import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Anchor,
  ButtonPrimary,
  ButtonSecondary,
  DatePicker,
  Heading,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ProgressCircle,
} from '@americanexpress/dls-react';
import {
  extractUpdateCustomerApplicationToken,
  useConfirmESignDisagreementRequest,
  useVerifyBirthdateRequest,
} from '../../../utils/APIs';
import ConsentStatement from './ConsentStatement';
import {
  createVerifyBirthdateValidationSchema,
  formatBirthdateForApi,
  formatBirthdateInput,
} from './validations';

export const CONSENT_DECISIONS = {
  AGREE: 'agree',
  DISAGREE: 'disagree',
};

const CONSENT_STEPS = {
  CONSENT: 'consent',
  VERIFY_BIRTHDATE: 'verify-birthdate',
  DISAGREEMENT: 'disagreement',
  ELETTER_NOT_FOUND: 'eletter-not-found',
};

const ESignConsent = ({
  isOpen,
  onClose,
  onConsentSuccess,
  setHasConsented,
  onConsentDenied,
  hasConsented,
  pcn,
}) => {
  const intl = useIntl();
  const [showConsentStatement, setShowConsentStatement] = useState(false);
  const [currentStep, setCurrentStep] = useState(CONSENT_STEPS.CONSENT);
  const [birthdate, setBirthdate] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  const validationSchema = createVerifyBirthdateValidationSchema({
    requiredMessage: intl.formatMessage({ id: 'verify.birthdate.required' }),
    invalidMessage: intl.formatMessage({ id: 'verify.birthdate.invalid' }),
  });

  const { runVerifyBirthdateRequest } = useVerifyBirthdateRequest(
    pcn,
    birthdate ? formatBirthdateForApi(birthdate) : '',
  );
  const { runConfirmESignDisagreementRequest } =
    useConfirmESignDisagreementRequest(pcn);

  const handleAgree = () => {
    setServiceError('');
    setCurrentStep(CONSENT_STEPS.VERIFY_BIRTHDATE);
  };

  const handleDisagree = () => {
    setServiceError('');
    setFieldError('');
    setCurrentStep(CONSENT_STEPS.DISAGREEMENT);
  };

  const handleDateChange = (_event, { dateString }) => {
    setBirthdate(formatBirthdateInput(dateString));
    if (fieldError) setFieldError('');
    if (serviceError) setServiceError('');
  };

  // eslint-disable-next-line complexity -- complexity is high due to multiple branches of logic for handling search results and errors
  const handleVerifySubmit = async () => {
    try {
      await validationSchema.validate({ birthdate }, { abortEarly: true });
      setFieldError('');
    } catch (error) {
      setFieldError(error.message);
      return;
    }

    setIsSubmitting(true);
    setServiceError('');

    try {
      const { data, error: apiError } = await runVerifyBirthdateRequest();

      if (data?.status === 403) {
        if (!isMountedRef.current) return;
        setServiceError(
          intl.formatMessage({ id: 'verify.birthdate.error.locked' }),
        );
      } else if (apiError) {
        if (!isMountedRef.current) return;
        setServiceError(
          intl.formatMessage({ id: 'verify.birthdate.error.service' }),
        );
      } else if (data?.body?.status === 'SUCCESS' && !data?.body?.token) {
        setCurrentStep(CONSENT_STEPS.ELETTER_NOT_FOUND);
      } else if (data?.body?.status === 'SUCCESS') {
        if (!isMountedRef.current) return;
        const verifyToken = extractUpdateCustomerApplicationToken(data);
        if (!verifyToken) {
          setServiceError(
            intl.formatMessage({ id: 'verify.birthdate.error.service' }),
          );
          return;
        }
        setHasConsented(true);
        onConsentSuccess(verifyToken);
      } else {
        if (!isMountedRef.current) return;
        setServiceError(
          intl.formatMessage({ id: 'verify.birthdate.error.service' }),
        );
      }
    } catch {
      if (!isMountedRef.current) return;
      setServiceError(
        intl.formatMessage({ id: 'verify.birthdate.error.service' }),
      );
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setBirthdate('');
      }
    }
  };

  const handleBackToConsent = () => {
    if (hasConsented) {
      onClose();
      return;
    }

    setCurrentStep(CONSENT_STEPS.CONSENT);
    setFieldError('');
    setServiceError('');
    setBirthdate('');
  };

  const handleConfirmDisagreement = async () => {
    setIsSubmitting(true);

    try {
      await runConfirmESignDisagreementRequest();
    } catch {
      // Continue with flow even if the request fails.
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setHasConsented(false);
        onConsentDenied();
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setShowConsentStatement(false);
      setCurrentStep(
        hasConsented ? CONSENT_STEPS.VERIFY_BIRTHDATE : CONSENT_STEPS.CONSENT,
      );
      setBirthdate('');
      setFieldError('');
      setServiceError('');
      setIsSubmitting(false);
    }
  }, [isOpen, hasConsented]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  if (!isOpen) return null;

  return (
    <Modal
      id="esign-consent-modal"
      aria-labelledby="esign-consent-heading"
      aria-describedby="esign-consent-description"
      onClose={onClose}
    >
      <ModalHeader
        id="esign-consent-heading"
        closeButtonLabel={intl.formatMessage({ id: 'esign.consent.close' })}
        theme={{
          position: 'relative',
          justifyContent:
            currentStep === CONSENT_STEPS.ELETTER_NOT_FOUND
              ? 'flex-end'
              : 'center',
          minHeight: '3.5rem',
        }}
      >
        {currentStep !== CONSENT_STEPS.ELETTER_NOT_FOUND ? (
          <h2 className="fluid heading-4">
            {intl.formatMessage({ id: 'esign.consent.heading' })}
          </h2>
        ) : null}
      </ModalHeader>

      <ModalBody id="esign-consent-description">
        {currentStep === CONSENT_STEPS.CONSENT ? (
          <div className="pad-4">
            <Heading level={3} headingStyle="heading5" className="pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.terms.heading' })}
            </Heading>

            <p className="body-3 pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.terms.intro' })}
            </p>

            <p className="body-2 pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.terms.note' })}
            </p>

            <p className="body-2 pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.description' })}{' '}
              <Anchor
                onClick={() => setShowConsentStatement(true)}
                inline={true}
              >
                {intl.formatMessage({ id: 'esign.consent.statement.link' })}
              </Anchor>
              .
            </p>

            <p className="body-2 pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.terms.disagree' })}
            </p>
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.VERIFY_BIRTHDATE ? (
          <div className="pad-4" style={{ textAlign: 'center' }}>
            <Heading
              level={3}
              headingStyle="heading5"
              className="pad-2-b"
              style={{ textAlign: 'center' }}
            >
              {intl.formatMessage({ id: 'verify.birthdate.heading' })}
            </Heading>

            <p className="body-3 pad-2-b">
              {intl.formatMessage({ id: 'verify.birthdate.description' })}
            </p>

            <div className="pad-2-b">
              <div className="d-flex align-items-center justify-content-center mar-1-b">
                <Label htmlFor="birthdate" style={{ marginBottom: 0 }}>
                  {intl.formatMessage({ id: 'verify.birthdate.label' })}
                </Label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <DatePicker
                  id="birthdate"
                  name="birthdate"
                  date={birthdate}
                  format="MM/DD/YYYY"
                  locale={intl.locale}
                  placeholder={intl.formatMessage({
                    id: 'verify.birthdate.placeholder',
                  })}
                  onDateChange={handleDateChange}
                  onPaste={(e) => e.preventDefault()}
                  status={fieldError ? 'error' : 'default'}
                  describedBy={fieldError ? 'birthdate-error' : undefined}
                />
              </div>
              {fieldError && (
                <p
                  id="birthdate-error"
                  className="dls-color-warning"
                  role="alert"
                >
                  {fieldError}
                </p>
              )}
            </div>

            {isSubmitting && (
              <div className="pad-2-t" style={{ textAlign: 'center' }}>
                <ProgressCircle
                  determinate={false}
                  label={intl.formatMessage({ id: 'verify.birthdate.loading' })}
                />
              </div>
            )}

            {serviceError && (
              <p className="pad-2-t dls-color-warning" role="alert">
                {serviceError}
              </p>
            )}
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.DISAGREEMENT ? (
          <div className="pad-4" style={{ textAlign: 'center' }}>
            <Heading level={3} headingStyle="heading5" className="pad-2-b">
              {intl.formatMessage({ id: 'esign.consent.heading' })}
            </Heading>

            <p className="body-2 pad-3-b">
              {intl.formatMessage({
                id: 'esign.consent.did.not.consent',
                defaultMessage:
                  'Sorry, in order to view the decision details of your application, you need to agree to accept electronic communication. You will receive your decision details in the mail.',
              })}
            </p>
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.ELETTER_NOT_FOUND ? (
          <div className="pad-4" style={{ textAlign: 'center' }}>
            <p className="body-2 pad-3" style={{ color: '#000' }} role="alert">
              {intl.formatMessage({
                id: 'verify.birthdate.error.not.found',
              })}
            </p>
          </div>
        ) : null}
      </ModalBody>
      {showConsentStatement && (
        <ConsentStatement
          isOpen={showConsentStatement}
          onClose={() => setShowConsentStatement(false)}
        />
      )}
      <ModalFooter>
        {currentStep === CONSENT_STEPS.CONSENT ? (
          <div className="row">
            <div className="col-md-6 d-flex justify-content-start">
              <ButtonSecondary type="button" size="sm" onClick={handleDisagree}>
                {intl.formatMessage({ id: 'esign.consent.disagree' })}
              </ButtonSecondary>
            </div>
            <div>
              <ButtonPrimary type="button" size="sm" onClick={handleAgree}>
                {intl.formatMessage({ id: 'esign.consent.agree' })}
              </ButtonPrimary>
            </div>
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.VERIFY_BIRTHDATE ? (
          <div className="row">
            <div className="col-md-6 d-flex justify-content-start">
              <ButtonSecondary
                type="button"
                size="sm"
                onClick={handleBackToConsent}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: 'verify.birthdate.back' })}
              </ButtonSecondary>
            </div>
            <div>
              <ButtonPrimary
                type="button"
                size="sm"
                onClick={handleVerifySubmit}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: 'verify.birthdate.submit' })}
              </ButtonPrimary>
            </div>
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.DISAGREEMENT ? (
          <div className="row">
            <div className="col-md-6 d-flex justify-content-start">
              <ButtonSecondary
                type="button"
                size="sm"
                onClick={handleBackToConsent}
                disabled={isSubmitting}
              >
                {intl.formatMessage({
                  id: 'esign.disagreement.return',
                  defaultMessage: 'Return to E-Sign Consent Page',
                })}
              </ButtonSecondary>
            </div>
            <div>
              <ButtonPrimary
                type="button"
                size="sm"
                onClick={handleConfirmDisagreement}
                disabled={isSubmitting}
              >
                {intl.formatMessage({
                  id: 'esign.disagreement.confirm',
                  defaultMessage: 'Confirm E-sign Disagreement',
                })}
              </ButtonPrimary>
            </div>
          </div>
        ) : null}

        {currentStep === CONSENT_STEPS.ELETTER_NOT_FOUND ? (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <ButtonSecondary type="button" size="sm" onClick={onClose}>
              {intl.formatMessage({ id: 'search.page.exit' })}
            </ButtonSecondary>
          </div>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};

ESignConsent.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConsentSuccess: PropTypes.func,
  onConsentDenied: PropTypes.func,
  hasConsented: PropTypes.bool,
  pcn: PropTypes.string,
  setHasConsented: PropTypes.func.isRequired,
};

ESignConsent.defaultProps = {
  isOpen: false,
  onClose: () => {},
  onConsentSuccess: () => {},
  onConsentDenied: () => {},
  hasConsented: false,
  pcn: null,
};

export default ESignConsent;
